/**
 * 일정 화면 패널 너비 조절: 각 패널 오른쪽 끝에 손잡이를 단다.
 * 끌기 또는 방향키(Shift 는 40px)로 조절, 두 번 클릭이나 Home 키로 원래 크기. 크기는 이 브라우저에만 기억한다.
 * 왼쪽 패널들의 너비는 함께 바뀐다(사이드바 너비). 높이는 화면에 맞춰 자동으로 나누므로(calendar.css) 손잡이를 두지 않는다.
 */
const KEY = 'selfon.calendar.panels.v1';
type Sizes = Record<string, number>;
const load = (): Sizes => { try { return JSON.parse(localStorage.getItem(KEY) ?? '{}') as Sizes; } catch { return {}; } };
const save = (s: Sizes) => { try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* 저장 불가 환경 */ } };
const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

function setup(workspace: HTMLElement, sizes: Sizes) {
  workspace.querySelectorAll<HTMLElement>('.schedule-panel').forEach((panel) => {
    if (panel.querySelector(':scope > .panel-resize-handle')) return;
    const sidebar = Boolean(panel.closest('.cal-side'));
    const label = panel.getAttribute('aria-label') ?? '일정';
    {
      const id = sidebar ? 'sidebar-x' : `${label}-x`;
      const handle = document.createElement('div');
      handle.className = 'panel-resize-handle panel-resize-x';
      handle.tabIndex = 0;
      handle.setAttribute('role', 'separator');
      handle.setAttribute('aria-orientation', 'vertical');
      handle.setAttribute('aria-label', `${label} 너비 조절`);
      handle.title = '끌거나 방향키로 조절 · 두 번 클릭하면 원래 크기';
      const limits = (): [number, number] => sidebar ? [200, Math.max(200, Math.min(500, workspace.clientWidth - 300))]
        : [Math.min(280, panel.parentElement!.clientWidth), panel.parentElement!.clientWidth];
      const size = () => (sidebar ? (workspace.querySelector('.mobile-filter') ?? panel) : panel).getBoundingClientRect().width;
      const apply = (value: number, remember = true) => {
        const [min, max] = limits();
        const n = Math.round(clamp(value, min, max));
        if (sidebar) { workspace.dataset.userWidth = ''; workspace.style.setProperty('--panel-sidebar-width', `${n}px`); }
        else panel.style.width = `${n}px`;
        handle.setAttribute('aria-valuemin', String(min)); handle.setAttribute('aria-valuemax', String(max)); handle.setAttribute('aria-valuenow', String(n));
        if (remember) { sizes[id] = n; save(sizes); }
      };
      const reset = () => {
        if (sidebar) { delete workspace.dataset.userWidth; workspace.style.removeProperty('--panel-sidebar-width'); }
        else panel.style.removeProperty('width');
        delete sizes[id]; save(sizes);
        handle.setAttribute('aria-valuenow', String(Math.round(size())));
      };
      let drag: { id: number; start: number; size: number } | null = null;
      handle.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        drag = { id: e.pointerId, start: e.clientX, size: size() };
        try { handle.setPointerCapture(e.pointerId); } catch { /* 포인터가 이미 끝난 경우 */ }
      });
      handle.addEventListener('pointermove', (e) => { if (drag?.id === e.pointerId) apply(drag.size + e.clientX - drag.start); });
      const end = () => { drag = null; };
      handle.addEventListener('pointerup', end); handle.addEventListener('pointercancel', end); handle.addEventListener('lostpointercapture', end);
      handle.addEventListener('dblclick', reset);
      handle.addEventListener('keydown', (e) => {
        if (e.key === 'Home') { e.preventDefault(); reset(); return; }
        const delta = ({ ArrowLeft: -1, ArrowRight: 1 } as Record<string, number>)[e.key];
        if (delta) { e.preventDefault(); apply(size() + delta * (e.shiftKey ? 40 : 10)); }
      });
      panel.append(handle);
      if (sizes[id] !== undefined) apply(sizes[id]!, false);
      const [min, max] = limits();
      handle.setAttribute('aria-valuemin', String(min)); handle.setAttribute('aria-valuemax', String(max)); handle.setAttribute('aria-valuenow', String(Math.round(size())));
    }
  });
}

/** 작업 영역이 다시 그려질 때마다(패널이 새로 생기면) 손잡이를 붙인다. 반환값으로 정리한다. */
export function attachPanelResize(workspace: HTMLElement): () => void {
  const sizes = load();
  // 예전에 끌어서 정한 높이는 더 쓰지 않는다(남아 있으면 패널이 잘려 보였다)
  const stale = Object.keys(sizes).filter((k) => k.endsWith('-y'));
  if (stale.length) { stale.forEach((k) => delete sizes[k]); save(sizes); }
  let queued = false;
  const queue = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; setup(workspace, sizes); });
  };
  const obs = new MutationObserver(queue);
  obs.observe(workspace, { childList: true, subtree: true });
  queue();
  return () => obs.disconnect();
}
